// ---------------------------------------------------------------------------
// Garder le contenu pré-généré à l'écran jusqu'à ce que l'application prenne
// le relais.
//
// Une page SSG arrive avec son contenu DANS `#orbit_front_app`. Or React vide
// son conteneur au premier commit : le contenu disparaissait donc le temps que
// l'application se monte et rende ses blocs. Avec l'amorçage des réponses
// (`primeRequestCache`) ce trou est passé de ~1 s à ~150 ms, mais il reste
// visible — un clignotement sur la moitié haute de la page.
//
// La parade ne touche ni React ni les composants : on SORT le contenu
// pré-généré du conteneur avant le montage, on le laisse peint à sa place, et
// on l'efface quand l'application a rendu l'équivalent. React démarre donc dans
// un conteneur vide, comme sur une page ordinaire — il n'a rien à effacer.
//
// Le contenu sorti n'est PAS interactif (aucun gestionnaire React n'y est
// attaché) : seuls les liens y fonctionnent. C'est acceptable pour la fraction
// de seconde qu'il vit, et c'est déjà ce qu'était le contenu pré-généré avant
// le montage.
// ---------------------------------------------------------------------------

/** Part au bout de ce délai même si l'application n'a rien rendu. */
const ABANDON_MS = 5000;

/** Ce que l'application doit avoir rendu, en part du texte pré-généré. */
const SEUIL = 0.5;

/** Les champs de saisie, dans l'ordre du document. */
const CHAMPS = 'input:not([type="hidden"]), textarea, select';

/** Ce qu'un visiteur peut atteindre au clavier ou cliquer. */
const ATTEIGNABLES = 'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** Balises dont le texte n'est pas du contenu affiché. */
const NON_AFFICHE = new Set(['STYLE', 'SCRIPT', 'NOSCRIPT', 'TEMPLATE']);

/**
 * La longueur du texte AFFICHÉ d'une racine. `textContent` compte aussi le
 * contenu des `<style>` : un bloc de site embarque sa feuille (plusieurs ko,
 * rendue en tête de son arbre), qui atteignait à elle seule le seuil — l'îlot
 * reprenait la main avant d'avoir rendu ses diapositives (chargées à part), et
 * le carrousel de l'accueil Ostrum restait vide ~300 ms : un clignotement.
 */
function longueurTexte(racine) {
  let n = 0;
  const parcours = document.createTreeWalker(racine, NodeFilter.SHOW_TEXT, {
    acceptNode: (noeud) => (NON_AFFICHE.has(noeud.parentNode?.nodeName)
      ? NodeFilter.FILTER_REJECT
      : NodeFilter.FILTER_ACCEPT),
  });
  while (parcours.nextNode()) n += parcours.currentNode.nodeValue.trim().length;
  return n;
}

/** Au-delà, la main est rendue même si une image n'est pas encore décodée. */
const ATTENTE_IMAGES_MS = 1500;

/**
 * Résolue quand les images du rendu que le contenu peint AFFICHE déjà (même
 * adresse, chargées) sont décodées — ou au bout de `ATTENTE_IMAGES_MS`. Les
 * autres (paresseuses, pas encore chargées) ne retiennent rien : le contenu
 * peint ne les montrait pas non plus.
 */
function imagesPretes(ghost, container) {
  const peintes = new Set();
  ghost.querySelectorAll('img').forEach((img) => {
    if (img.complete && img.naturalWidth > 0) {
      peintes.add(img.currentSrc);
      peintes.add(img.src);
    }
  });
  peintes.delete('');
  const attentes = [...container.querySelectorAll('img')]
    .filter((img) => peintes.has(img.currentSrc) || peintes.has(img.src))
    .map((img) => {
      // Le conteneur est en `display: none` : une image `loading="lazy"` n'y
      // partirait jamais. Celle-ci est déjà dans le cache : rien à télécharger.
      if (img.loading === 'lazy') img.loading = 'eager';
      return img.decode().catch(() => {});
    });
  if (!attentes.length) return Promise.resolve();
  return Promise.race([
    Promise.all(attentes),
    new Promise((fin) => { setTimeout(fin, ATTENTE_IMAGES_MS); }),
  ]);
}

/**
 * Les éléments atteignables RÉELLEMENT affichés. Le rendu de l'application
 * porte des affordances d'édition masquées (`.builder-edit-only`) que le HTML
 * généré n'a plus : compter les seuls éléments visibles aligne les deux listes.
 */
const atteignables = (racine) => [...racine.querySelectorAll(ATTEIGNABLES)]
  .filter((el) => el.getClientRects().length > 0);

/**
 * Le curseur d'un champ texte, `null` ailleurs (`email`, `number` n'exposent
 * pas de sélection : la lire y rend `null` ou lève selon le navigateur).
 */
function lireCurseur(el) {
  try {
    return typeof el?.selectionStart === 'number' ? [el.selectionStart, el.selectionEnd] : null;
  } catch {
    return null;
  }
}

/** Remet le curseur lu par `lireCurseur` ; sans curseur lisible, en fin de saisie. */
function poserCurseur(el, curseur) {
  try {
    const fin = el.value?.length ?? 0;
    el.setSelectionRange(...(curseur ?? [fin, fin]));
  } catch { /* type sans sélection : le navigateur garde la sienne */ }
}

/**
 * Reporte dans le rendu de l'application ce que le visiteur a SAISI dans le
 * contenu peint : il a pu commencer à taper avant que le JavaScript arrive, et
 * remplacer le contenu effaçait sa saisie (vu sur le formulaire du livre blanc
 * AODB, réseau lent). Les champs se correspondent par leur rang. La valeur
 * passe par le setter NATIF puis un événement `input` : c'est ce qui la fait
 * remonter dans l'état de React, qui ignore une simple affectation de `.value`.
 */
function reporterSaisie(source, cible) {
  const avant = [...source.querySelectorAll(CHAMPS)];
  const apres = [...cible.querySelectorAll(CHAMPS)];
  avant.forEach((champ, i) => {
    const relais = apres[i];
    if (!relais || relais.tagName !== champ.tagName || relais.type !== champ.type) return;
    if (champ.type === 'checkbox' || champ.type === 'radio') {
      if (champ.checked !== champ.defaultChecked && relais.checked !== champ.checked) relais.click();
      return;
    }
    // Un `<select>` n'a pas de `defaultValue` : sa valeur d'origine est celle de
    // l'option `selected` du HTML (la première sinon). Sans ça, tout select passait
    // pour « saisi », et son `change` rejoué déclenchait ses actions au montage
    // (le sélecteur de part d'une fiche de fonds rechargeait la page).
    const origine = champ.tagName === 'SELECT'
      ? ([...champ.options].find((o) => o.defaultSelected) ?? champ.options[0])?.value
      : champ.defaultValue;
    if (champ.value === origine || relais.value === champ.value) return;
    const proto = Object.getPrototypeOf(relais);
    Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(relais, champ.value);
    relais.dispatchEvent(new Event('input', { bubbles: true }));
    relais.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/**
 * Sert pour la page entière (`#orbit_front_app`, reprise du SPA) comme pour un
 * seul bloc (un îlot, cf. `src/site/islands.jsx`). Un bloc est en
 * `display: contents` : le fantôme reprend ce mode, sans quoi il ajouterait une
 * boîte dans la grille ou le flex de son parent et décalerait la mise en page.
 *
 * @param {HTMLElement} container le conteneur que React va remplir
 * @param {object} [options]
 * @param {string|null} [options.ghostId] l'id du fantôme (aucun si null)
 * @param {number} [options.seuil] part du texte pré-généré à atteindre
 * @returns {boolean} vrai si un contenu pré-généré a été mis de côté
 */
export function holdSsgPaint(container, { ghostId = 'orbit_ssg_paint', seuil = SEUIL } = {}) {
  if (!container?.firstChild) return false;

  const ghost = document.createElement('div');
  if (ghostId) ghost.id = ghostId;
  if (getComputedStyle(container).display === 'contents') ghost.style.display = 'contents';
  // Déplacer un nœud lui retire le focus : un visiteur qui tape dans le contenu
  // peint (un îlot s'éveille justement au focus d'un champ) verrait sa frappe
  // suivante tomber sur <body>. Le même nœud reprend le focus et son curseur
  // dans la même tâche — aucune frappe ne s'intercale.
  const actif = container.contains(document.activeElement) ? document.activeElement : null;
  const curseur = lireCurseur(actif);
  while (container.firstChild) ghost.appendChild(container.firstChild);
  container.parentNode.insertBefore(ghost, container);
  if (actif?.isConnected) {
    actif.focus({ preventScroll: true });
    poserCurseur(actif, curseur);
  }

  const attendu = longueurTexte(ghost) * seuil;
  // Un formulaire n'est prêt que si TOUS ses champs sont rendus : le texte peut
  // atteindre le seuil avant le fragment qui porte les champs (chargé à part).
  const champsAttendus = ghost.querySelectorAll(CHAMPS).length;
  const pret = () => longueurTexte(container) >= attendu
    && container.querySelectorAll(CHAMPS).length >= champsAttendus;

  // Le contenu peint n'a aucun gestionnaire : un clic sur un de ses boutons
  // serait perdu, et un `<form>` partirait en GET vers la page elle-même. Le
  // geste est retenu et rejoué sur l'élément équivalent une fois la main rendue.
  // Clics faits AVANT que ce code soit chargé : la page générée les met en file
  // (`window.__orbitIslandClicks`, script en ligne posé par src/ssg/islands.js).
  let gesteEnAttente = (window.__orbitIslandClicks || []).filter((el) => ghost.contains(el)).pop() ?? null;
  const retenirClic = (e) => {
    const cible = e.target.closest?.(ATTEIGNABLES);
    if (!cible || cible.tagName === 'A') return; // un lien navigue tout seul
    e.preventDefault();
    gesteEnAttente = cible;
  };
  const bloquerEnvoi = (e) => e.preventDefault();
  ghost.addEventListener('click', retenirClic, true);
  ghost.addEventListener('submit', bloquerEnvoi, true);
  // Le conteneur reste dans le flux mais invisible : l'écran de chargement de
  // `BuilderPublicRenderer` ne doit pas s'afficher SOUS le contenu déjà peint.
  const displayInitial = container.style.display;
  container.style.display = 'none';

  let fini = false;
  const rendreLaMain = () => {
    if (fini) return;
    fini = true;
    observer.disconnect();
    clearTimeout(minuteur);
    container.style.display = displayInitial;
    reporterSaisie(ghost, container);
    // Un visiteur au clavier peut avoir déjà atteint un élément du contenu
    // peint : le retirer renverrait son focus sur <body> (WCAG 2.4.3). On le
    // rend à l'élément de même rang dans le rendu de l'application.
    const avant = atteignables(ghost);
    const apres = atteignables(container);
    const rangActif = avant.indexOf(document.activeElement);
    const curseurActif = lireCurseur(document.activeElement);
    const rangGeste = gesteEnAttente ? avant.indexOf(gesteEnAttente) : -1;
    ghost.removeEventListener('click', retenirClic, true);
    ghost.removeEventListener('submit', bloquerEnvoi, true);
    ghost.remove();
    if (rangActif >= 0 && apres[rangActif]) {
      apres[rangActif].focus({ preventScroll: true });
      // La saisie reportée se poursuit là où le visiteur l'avait laissée.
      if (avant[rangActif].matches(CHAMPS)) poserCurseur(apres[rangActif], curseurActif);
    }
    if (rangGeste >= 0) apres[rangGeste]?.click();
  };

  // Texte prêt ne suffit pas : les images du rendu sont des éléments NEUFS, et
  // un `<img>` neuf passe une image sans pixels même quand le fichier est en
  // cache (décodage asynchrone). Échanger aussitôt montrait le bandeau vide
  // puis sa photo « a posteriori » (carrousel de l'accueil Ostrum). On attend
  // donc le décodage des images que le contenu peint affiche déjà.
  let enAttente = false;
  const observer = new MutationObserver(() => {
    if (enAttente || fini || !pret()) return;
    enAttente = true;
    imagesPretes(ghost, container).then(() => {
      enAttente = false;
      if (pret()) rendreLaMain();
    });
  });
  observer.observe(container, { childList: true, subtree: true, characterData: true });

  // Au bout du délai, on ne rend la main QUE si l'application a rendu quelque
  // chose. Sinon — erreur JS, ressource manquante, appareil très lent — retirer
  // le contenu pré-généré remplacerait une page parfaitement lisible par une
  // page blanche : exactement l'inverse de ce que le statique sert à garantir.
  // L'observateur reste armé ; l'application peut encore arriver.
  const minuteur = setTimeout(() => {
    if ((container.textContent || '').trim().length > 0) rendreLaMain();
  }, ABANDON_MS);

  return true;
}
